import { useSubjectUpdate } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { atom, useRecoilState } from 'recoil'
import { useTheme } from 'styled-components'

import { Padding } from '@/@bothrs-ui/web/components/layout'
import { PaginatedView } from '@/@bothrs-ui/web/pages/paginated-view'
import { localStorageEffect } from '@/capacitor/recoil/localstorage-effect'
import { useHideNativeNavigation, useHideWebHeader } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { useTenantHref } from '@/modules/shared/hooks/useTenantHref'
import { StButton, StButtonContainer, StContainer, StScreenView } from './app-update.page.styled'
import { AppUpdateSlide } from './components/app-update'
import { getUpdateSlidesForVersion } from './update-slides'

export const localAppUpdateAtom = atom<boolean>({
  key: '@app/local-AppUpdate-slides',
  default: false,
  effects: [localStorageEffect('@app/local-AppUpdate-slides')],
})

const AppUpdatePage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const tenantHref = useTenantHref()
  const homePath = tenantHref('/')
  const [{ mutate }, updateSubject] = useSubjectUpdate()

  const [currentSlideInfo, setCurrentSlideInfo] = useState<[number, number]>([0, 1])
  const [, setLocalAppUpdateAtom] = useRecoilState(localAppUpdateAtom)

  // Get slides for current app version
  const slides = useMemo(() => {
    return getUpdateSlidesForVersion(APP_VERSION)
  }, [])

  //hide native navigation bar
  useHideWebHeader()
  useHideNativeNavigation()

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const { finishAppUpdate, handleContinue } = useMemo(() => {
    async function finishAppUpdate() {
      mixpanel.track('app_update_finished')
      mutate()
        .then((latestActor) =>
          updateSubject({
            meta: {
              ...latestActor?.meta,
              hasSeenAppUpdate: {
                version: APP_VERSION,
                date: new Date().toISOString(),
              },
            },
          }),
        )
        .finally(() => {
          mutate()

          setLocalAppUpdateAtom(true)
          navigate(homePath)
        })
    }
    function handleContinue() {
      setCurrentSlideInfo(([currentSlide, direction]) => [currentSlide + 1, direction])
    }
    return { finishAppUpdate, handleContinue }
  }, [mutate, navigate, setLocalAppUpdateAtom, updateSubject])

  return (
    <StScreenView>
      <StContainer>
        <PaginatedView currentSlideInfo={currentSlideInfo} setCurrentSlideInfo={setCurrentSlideInfo}>
          {slides.map((slide, index) => {
            return <AppUpdateSlide key={index} {...slide} />
          })}
        </PaginatedView>
        <StButtonContainer>
          {currentSlideInfo[0] < slides?.length - 1 ? (
            <StButton variant='Primary' type='submit' onClick={handleContinue}>
              {t({
                id: 'app-update.continue',
                comment: 'next page',
                message: 'Volgende',
              })}
            </StButton>
          ) : (
            <StButton variant='Primary' type='submit' onClick={finishAppUpdate}>
              {t({
                id: 'app-update.done',
                message: 'Sluiten',
              })}
            </StButton>
          )}
          <Padding py={theme.UI.Spacing.Space2} />
        </StButtonContainer>
      </StContainer>
    </StScreenView>
  )
}

export { AppUpdatePage }
